import classNames from 'classnames/bind';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from '@mantine/core';

import { CamerasWidget as CamerasWidgetType } from 'types/dashboard_widget';
import { Stream } from 'types/stream';

import { useCameras } from 'cameras/hooks/useCameras';
import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { useMqttCamerasState } from 'cameras/hooks/useMqttCamerasState';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import { useHasAccess } from 'hooks/useHasAccess';
import { useAPI } from 'hooks/api/useAPI';
import { useStreamsByCameras } from 'streams/hooks/useStreamsByCameras';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { DeployRoute, NavRoute } from 'application/types/routes';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';
import { WebRTCPlayer } from 'components/WebRTCPlayer/WebRTCPlayer';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './CamerasWidget.module.scss';

const c = classNames.bind(styles);

const isH264 = (stream: Stream) =>
  stream.name.toLowerCase().includes('h264') ||
  stream.name.toLowerCase().includes('h.264') ||
  stream.source === 'pipeline_stream';

const PAGE_SIZE = 50;

export function CamerasWidget(widget: CamerasWidgetType) {
  const { options, autoPlay } = widget;
  const { applicationID } = useAPI();
  const navigate = useNavigate();
  const [hasAccess] = useHasAccess();
  const [currentPage, setCurrentPage] = useState(1);

  const tagFilters = useTagsFilterParams();
  const { gateway_id } = useDashboardFilterParams();

  const { data: cameras } = useCameras({
    params: {
      ...options,
      ...tagFilters,
      gateway_ids: gateway_id
        ? Array.from(new Set([gateway_id, ...(options?.gateway_ids || [])]))
        : Array.from(new Set([...(options?.gateway_ids || [])])),
      page: currentPage,
      limit: PAGE_SIZE,
    },
  });

  useMqttCamerasState(cameras?.data ? cameras.data.map(({ id }) => id) : []);

  // Fetch streams only for non-virtual cameras since we can't play virtual cameras
  const nonVirtualCameras =
    cameras?.data?.filter((camera) => camera.conn_type !== 'virtual') || [];

  const shouldFetchStreams = nonVirtualCameras.length > 0;

  // Fetch streams individually for each non-virtual camera
  const { streams: allStreams } = useStreamsByCameras({
    cameras: nonVirtualCameras,
    sources: ['camera_stream', 'uri_stream', 'pipeline_stream'],
    stream_types: ['webrtc', 'rtsp'],
    limit: 50,
    enabled: shouldFetchStreams,
  });

  useMqttStreamsState(allStreams.map(({ id }) => id));

  // Helper to get the best stream for a camera
  const getStreamForCamera = (cameraId: string) => {
    if (allStreams.length === 0) return null;

    const cameraStreams = allStreams.filter(
      (stream) => stream.camera_id === cameraId
    );

    if (cameraStreams.length === 0) return null;

    // Prefer H.264 streams for playback
    const h264Streams = cameraStreams.filter(isH264);
    return h264Streams[0] || cameraStreams[0];
  };

  const hasMatchingCameras = cameras?.data && Boolean(cameras.data.length);
  const hasPagination = cameras && cameras.total_pages > 1;

  return (
    <Widget widget={widget}>
      {!hasMatchingCameras && (
        <EmptyView>No cameras matching the filter.</EmptyView>
      )}

      {hasMatchingCameras && (
        <div className={c('wrap')}>
          <div className={c('grid')}>
            {cameras.data.map((camera) => {
              if (!camera.id) return null;

              const isVirtual = camera.conn_type === 'virtual';
              const stream = getStreamForCamera(camera.id);

              const renderContent = () => {
                // AutoPlay mode with WebRTC for non-virtual cameras with streams
                if (autoPlay && !isVirtual && stream?.id) {
                  return (
                    <WebRTCPlayer
                      className={c('stream-player')}
                      streamID={stream.id}
                      auto
                    />
                  );
                }

                // AutoPlay mode for virtual cameras (show snapshot)
                if (autoPlay && isVirtual) {
                  return (
                    <div className={c('camera-player')}>
                      <VideoSourceSnapshot
                        className={c('grid-item-preview')}
                        source={camera}
                        camera={camera}
                        autoRefresh
                        compact
                        showSettingsButton={hasAccess('deploy_edit')}
                        onSettingsClick={() =>
                          navigate(
                            `/applications/${applicationID}/${NavRoute.DEPLOY}/${DeployRoute.CAMERAS}/${camera.id}`,
                            { state: { tab: 'settings' } }
                          )
                        }
                      />
                    </div>
                  );
                }

                // Non-autoplay mode (snapshot with play button overlay)
                return (
                  <VideoSourceSnapshot
                    className={c('grid-item-preview')}
                    source={stream || camera}
                    camera={camera}
                    autoRefresh
                    showSettingsButton={hasAccess('deploy_edit')}
                    onSettingsClick={() =>
                      navigate(
                        `/applications/${applicationID}/${NavRoute.DEPLOY}/${DeployRoute.CAMERAS}/${camera.id}`,
                        { state: { tab: 'settings' } }
                      )
                    }
                  />
                );
              };

              return (
                <div className={c('grid-item')} key={camera.id}>
                  {renderContent()}
                </div>
              );
            })}
          </div>

          {hasPagination && (
            <div className={c('pagination')}>
              <Pagination
                total={cameras.total_pages}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </Widget>
  );
}
